import { differenceInMinutes, format, subDays } from 'date-fns';

import { IconDate } from '@components/icons/date';
import { FilterConfigField } from '@components/table';
import { FilterData } from '@components/table/filters';

import { useStore } from 'models/Provider';

const datePrefix = (field: string) => {
  switch (field) {
    case 'hired':
      return 'date_of_hire';
    case 'rejected':
      return 'date_of_rejection';
    default:
      return 'date';
  }
};

const dateFieldToExternalQuery = (filter: FilterData) => {
  if (!filter.field || !filter.comparer) return {};

  const now = new Date();
  const prefix = datePrefix(filter.field as string);

  switch (filter.comparer) {
    case 'today': {
      const selectedDay = new Date();
      return { [prefix]: format(selectedDay, 'yyyy-MM-dd') };
    }
    case 'yesterday': {
      const selectedDay = subDays(now, 1);
      return { [prefix]: format(selectedDay, 'yyyy-MM-dd') };
    }
    case 'gt': {
      const xDaysAgo = differenceInMinutes(now, subDays(now, Number(filter.value)));
      return { [`${prefix}__lt`]: `now-${xDaysAgo}m` };
    }
    case 'eq': {
      const selectedDay = subDays(now, Number(filter.value));
      return { [prefix]: format(selectedDay, 'yyyy-MM-dd') };
    }
    case 'lt': {
      const xDaysAgo = differenceInMinutes(now, subDays(now, Number(filter.value)));
      return { [`${prefix}__gt`]: `now-${xDaysAgo}m` };
    }
    case 'after':
      return { [`${prefix}__gte`]: filter.value };
    case 'before':
      return { [`${prefix}__lt`]: filter.value };
    case 'on': {
      const selectedDay = new Date(filter.value as string);
      return { [prefix]: format(selectedDay, 'yyyy-MM-dd') };
    }
    default:
      return {};
  }
};

export const useDateFilter = (): FilterConfigField => {
  const { t } = useStore();

  return {
    id: 'date',
    label: t('applicants.filters.date.title'),
    icon: <IconDate />,
    type: 'category',
    fields: [
      {
        id: 'applied',
        label: t('applicants.filters.date.applied'),
        type: 'date',
        allowedAbsoluteComparers: ['after', 'on', 'before'],
        allowedRelativeComparers: ['today', 'yesterday', 'gt', 'eq', 'lt'],
        toExternalQuery: dateFieldToExternalQuery,
      },
      {
        id: 'hired',
        label: t('applicants.filters.date.hired'),
        type: 'date',
        allowedAbsoluteComparers: ['after', 'on', 'before'],
        allowedRelativeComparers: ['today', 'yesterday', 'gt', 'eq', 'lt'],
        toExternalQuery: dateFieldToExternalQuery,
      },
      {
        id: 'rejected',
        label: t('applicants.filters.date.rejected'),
        type: 'date',
        allowedAbsoluteComparers: ['after', 'on', 'before'],
        allowedRelativeComparers: ['today', 'yesterday', 'gt', 'eq', 'lt'],
        toExternalQuery: dateFieldToExternalQuery,
      },
    ],
  };
};
